import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FaLock, FaPhoneAlt, FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import Select from "react-select";
import Modal from "react-modal";
import PatientCreateNewAccount from "pages/SantTukaramHospital/PatientCreateNewAccount";

Modal.setAppElement("#root");

export default function CommonPatientLogin() {
  const navigate = useNavigate();

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showHospitalDropdown, setShowHospitalDropdown] = useState(false);
  const [hospitalName, setHospitalName] = useState([]);

  const customStyles = {
    content: {
      top: "60px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "min(720px, 92vw)",
      maxHeight: "80vh",
      borderRadius: "20px",
      padding: "0",
      overflow: "hidden",
    },
    overlay: {
      backgroundColor: "rgba(15,23,42,0.65)",
      backdropFilter: "blur(6px)",
      zIndex: 1000,
    },
  };

  const validationSchema = Yup.object({
    mobilenumber: Yup.string()
      .matches(/^[0-9]+$/, "Mobile number must contain only digits")
      .length(10, "Mobile number must be exactly 10 digits")
      .required("Mobile Number is Required"),
    login_password: Yup.string().required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      hospital_id: "",
      mobilenumber: "",
      login_password: "",
      hospital_name: "",
    },
    validationSchema,
    onSubmit: handleSubmit2,
  });

  function onHandleSubmit(values, config) {
    axios
      .post("/api/authentication/patient-api/patient-login", values, config)
      .then((response) => {
        sessionStorage.setItem("token", response.data.token);
        sessionStorage.setItem("status", response.data.status);
        sessionStorage.setItem("userId", response.data.userId);

        if (response.data.message === "Login Successfully!") {
          toast.success(response.data.message, {
            autoClose: 500,
            onClose: () => navigate("/patient/allFamilyMembers"),
          });
        } else {
          if (!values.hospital_id) {
            toast.warning("Please select hospital", { autoClose: 1000 });
          } else {
            toast.error(response.data.message, { autoClose: 2000 });
          }
        }
      })
      .catch(() => toast.error("Login failed"));
  }

  function handleSubmit2(values, { setSubmitting }) {
    setLoading(true);
    axios
      .post("/api/authentication/patient-api/get-patient-for-login", values)
      .then((response) => {
        const hospitals = response.data.patientSignUpMasterList || [];
        setHospitalName(hospitals);

        if (!hospitals.length) {
          toast.error("Patient does not exist", { autoClose: 1000 });
          return;
        }

        const token = response.data.token;
        const config = { headers: { Authorization: `Bearer ${token}` } };

        if (hospitals.length === 1) {
          const patientData = hospitals[0];
          const payload = { ...values, hospital_id: patientData.hospital_id };
          onHandleSubmit(payload, config);
        } else {
          setShowHospitalDropdown(true);
          onHandleSubmit(values, config);
        }
      })
      .catch(() => toast.error("Something went wrong"))
      .finally(() => {
        setLoading(false);
        setSubmitting(false);
      });
  }

  return (
    <>
      <form onSubmit={formik.handleSubmit} className="space-y-4">
        {/* Mobile */}
        <div>
          <div className="relative">
            <FaPhoneAlt className="absolute left-3 top-2.5 text-gray-400" />
            <input
              name="mobilenumber"
              type="tel"
              inputMode="numeric"
              placeholder="Mobile Number"
              value={formik.values.mobilenumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full pl-10 pr-4 border rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none"
            />
          </div>
          {formik.touched.mobilenumber && formik.errors.mobilenumber && (
            <p className="text-red-500 text-xs mt-1 ml-1">
              {formik.errors.mobilenumber}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="relative">
            <FaLock className="absolute left-3 top-3.5 text-gray-400" />
            <input
              name="login_password"
              type={showPass ? "text" : "password"}
              placeholder="Password"
              value={formik.values.login_password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="w-full pl-10 pr-10 border rounded-xl focus:ring-2 focus:ring-indigo-400 outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPass((s) => !s)}
              className="absolute right-3 top-3.5 text-gray-400"
            >
              {showPass ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {formik.touched.login_password && formik.errors.login_password && (
            <p className="text-red-500 text-xs mt-1 ml-1">
              {formik.errors.login_password}
            </p>
          )}
        </div>

        {/* Hospital Select */}
        {showHospitalDropdown && (
          <div>
            <label className="text-sm font-medium text-gray-700 ml-1">
              Select Hospital
            </label>
            <Select
              options={hospitalName.map((h) => ({
                label: h.hospital_name,
                value: h.hospital_id,
              }))}
              onChange={(opt) => formik.setFieldValue("hospital_id", opt.value)}
              placeholder="Select Hospital"
            />
          </div>
        )}

        {/* Login */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-indigo-600 to-cyan-500 text-white py-2.5 rounded-xl font-semibold shadow-lg hover:from-indigo-700 hover:to-cyan-600"
        >
          {loading ? "Logging in..." : "Login"}
        </button>

        {/* <div className="text-center text-xs text-gray-400 pt-2">
          🔒 Secure Patient Access • Private & Encrypted
        </div> */}
      </form>

      {/* Links */}
      <div className="mt-2 flex justify-between items-center text-sm">
        <button
          type="button"
          onClick={() => setOpenCreateModal(true)}
          className="text-indigo-600 hover:underline font-medium"
        >
          Create new account
        </button>
        <button
          type="button"
          onClick={() => navigate("/forgotPatientPassword")}
          className="text-gray-500 hover:text-indigo-600 hover:underline"
        >
          Forgot Password
        </button>
      </div>

      {/* Modal */}
      <Modal
        isOpen={openCreateModal}
        onRequestClose={() => setOpenCreateModal(false)}
        shouldCloseOnOverlayClick
        style={customStyles}
      >
        <div className="bg-white rounded-2xl p-6">
          <PatientCreateNewAccount onClose={() => setOpenCreateModal(false)} />
        </div>
      </Modal>
    </>
  );
}